<template>
  <div class="fts-settings-page fts-sources">
    <header class="fts-settings-head">
      <div>
        <h1>Data sources</h1>
        <p>
          Databases this workspace reads modules from.
          <a href="#" class="fts-sources-autocount" @click.prevent="goAutoCount">AutoCount has its own page.</a>
        </p>
      </div>
    </header>

    <div class="fts-settings-loading" v-if="loading">
      <fts-spinner :size="16" />
      <span>Loading&hellip;</span>
    </div>

    <!-- An environment whose API predates this page says so, as the other settings pages do. -->
    <div class="fts-settings-banner is-warn" v-else-if="unavailable">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
      <span>{{ unavailable }}</span>
    </div>

    <template v-else>
      <!-- Inistate's own switch for every connector: nothing here can change it. -->
      <div class="fts-settings-banner is-warn fts-sources-platform-off" role="status" v-if="connectorsOff">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>
          <strong>Connectors are off for now</strong>
          {{ connectorsOff }}
        </span>
      </div>

      <div class="fts-settings-banner is-warn" role="alert" v-if="error">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>{{ error }}</span>
      </div>

      <section class="fts-settings-card" aria-label="Data sources" v-if="loaded">
        <p class="fts-sources-empty" v-if="!rows.length">
          No data source yet. <a href="#" @click.prevent="goAutoCount">Set up AutoCount</a>, or ask Inistate to
          connect another database.
        </p>

        <div class="fts-sources-scroll" v-else>
          <table class="fts-settings-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Reached</th>
                <th>Sign-in</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.name">
                <td>
                  <strong>{{ row.name }}</strong>
                  <small v-if="row.database">{{ row.database }}<template v-if="row.server"> on {{ row.server }}</template></small>
                </td>
                <td>{{ row.kind }}</td>
                <td>{{ row.reached }}</td>
                <td>{{ row.signIn }}</td>
                <td>
                  <span class="fts-settings-pill" :class="row.tone" v-if="row.viaAgent">{{ row.status }}</span>
                  <span class="is-muted" v-else>&mdash;</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="fts-settings-card" aria-labelledby="fts-sources-agents" v-if="agentRows.length">
        <div>
          <h2 id="fts-sources-agents">Connectors</h2>
          <p class="fts-settings-card-sub">
            The Inistate Connectors that joined this workspace from a PC on your network. Approve one only
            if its fingerprint matches the connector window on that PC.
          </p>
        </div>

        <div class="fts-settings-row fts-sources-agent" v-for="agent in agentRows" :key="agent.agentId">
          <span class="fts-settings-row-text">
            <strong>{{ agent.machineName }}</strong>
            <span>
              Fingerprint <span class="fts-sources-mono">{{ agent.fingerprint || "unknown" }}</span><template v-if="agent.lastContact"> &middot; last contact {{ agent.lastContact }}</template>
            </span>
            <span class="fts-sources-turned-off" v-if="agent.turnedOff">{{ agent.turnedOffLine }}</span>
          </span>
          <span class="fts-sources-agent-side">
            <span class="fts-settings-pill" :class="agent.tone">{{ agent.status }}</span>
            <button
              v-if="agent.turnedOff"
              type="button"
              class="fts-settings-btn is-primary"
              :disabled="Boolean(busyId)"
              @click="turnOn(agent)"
            >
              {{ busyId === agent.agentId ? "Turning on…" : "Turn on" }}
            </button>
            <!-- An AutoCount connector is approved where its connection is made as well. -->
            <a
              v-else-if="agent.pending && agent.autocount"
              href="#"
              class="fts-sources-approve-autocount"
              @click.prevent="goAutoCount"
            >Approve on the AutoCount page</a>
            <button
              v-else-if="agent.pending"
              type="button"
              class="fts-settings-btn is-primary"
              :disabled="Boolean(busyId)"
              @click="approve(agent)"
            >
              {{ busyId === agent.agentId ? "Approving…" : "Approve" }}
            </button>
            <button
              v-else
              type="button"
              class="fts-settings-btn"
              :disabled="Boolean(busyId)"
              @click="turnOff(agent)"
            >
              {{ busyId === agent.agentId ? "Turning off…" : "Turn off" }}
            </button>
          </span>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
/**
 * Settings › Data sources (docs/integration-data-sources-ux-proposal.md §3.2 in the SVN repo): the
 * plain list. Every database the workspace reads modules from, how each is reached, and the
 * connectors on the customer's network that serve them. AutoCount's guided setup is its own page,
 * and an AutoCount connector is approved there, where its connection is made too; this one
 * approves the others, and turns any connector off and on again.
 */
import FTSSpinner from "../../elements/FTSSpinner";
import api from "../sourceConnectionsApi";
import { timeAgo } from "../sourceTime";

const UNAVAILABLE =
  "Data sources are not on this environment yet. This page works once it runs an API that has them.";

function sameId(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

export default {
  name: "SettingsDataSources",
  components: { "fts-spinner": FTSSpinner },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      loaded: false,
      unavailable: "",
      error: "",
      connections: [],
      agents: [],
      // Inistate's switch for every connector: why they are off, or "".
      connectorsOff: "",
      // The connector being approved or turned off or on.
      busyId: "",
      // The connector just turned on, until its connections are online again.
      turningOn: "",
      now: Date.now(),
    };
  },
  computed: {
    rows() {
      return this.connections.map((connection) => {
        const sql = connection.sql || {};
        const link = connection.agent || {};
        const viaAgent = connection.transport === "agent";
        const agent = viaAgent ? this.agents.find((a) => sameId(a.agentId, link.agentId)) : null;
        // Turned off (its connector, or every connector by Inistate) says so, not merely "Offline".
        const off = Boolean(this.connectorsOff || (agent && agent.turnedOffUtc));
        const online = !off && Boolean(link.online);
        let status = online ? "Online" : "Offline";
        if (off) status = "Turned off";
        else if (!online && this.turningOn && sameId(this.turningOn, link.agentId)) status = "Turning on…";
        return {
          name: connection.name,
          database: sql.database || "",
          server: sql.server || "",
          kind: connection.kind === "autocount" ? "AutoCount" : "SQL Server",
          reached: viaAgent
            ? "Through " + ((agent && agent.machineName) || link.agentId || "a connector")
            : "Direct",
          signIn: sql.user ? "SQL login " + sql.user : "Windows account",
          viaAgent,
          online,
          status,
          tone: off ? "is-warn" : online ? "is-ok" : "",
        };
      });
    },
    agentRows() {
      return this.agents.map((agent) => {
        const pending = agent.status !== "approved";
        const turnedOff = Boolean(agent.turnedOffUtc);
        const when = timeAgo(agent.turnedOffUtc, this.now);
        let status = "Approved";
        let tone = "is-ok";
        if (turnedOff) {
          status = "Turned off";
          tone = "is-warn";
        } else if (pending) {
          status = "Waiting for approval";
          tone = "is-warn";
        }
        return {
          agentId: agent.agentId,
          machineName: agent.machineName || agent.agentId,
          fingerprint: agent.fingerprint || "",
          pending,
          autocount: agent.kind === "autocount",
          turnedOff,
          turnedOffLine: (agent.turnedOffBy ? "Turned off by " + agent.turnedOffBy : "Turned off")
            + (when ? ", " + when : ""),
          status,
          tone,
          lastContact: timeAgo(agent.lastSeenUtc, this.now),
        };
      });
    },
  },
  methods: {
    message(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && (data.message || data.error_description)) || fallback;
    },
    async load() {
      this.loading = !this.loaded;
      this.unavailable = "";
      const workspaceId = this.workspaceId;
      try {
        const res = await api.getSourceConnections(workspaceId);
        if (workspaceId !== this.workspaceId) return;
        const data = (res && res.data) || {};
        this.connections = Array.isArray(data.connections) ? data.connections : [];
        this.agents = Array.isArray(data.agents) ? data.agents : [];
        this.connectorsOff = data.connectorsOff || "";
        this.now = Date.now();
        this.loaded = true;
        this.error = "";
      } catch (err) {
        if (workspaceId !== this.workspaceId) return;
        const status = err && err.response && err.response.status;
        if (status === 404) this.unavailable = UNAVAILABLE;
        else this.error = this.message(err, "The data sources are not available right now.");
      } finally {
        this.loading = false;
      }
    },
    async approve(agent) {
      if (this.busyId) return;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Approve " + agent.machineName + "?",
        text: "Approve only if the connector window on " + agent.machineName + " shows this fingerprint: "
          + (agent.fingerprint || "unknown") + ".",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: "Approve", default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true) return;
      await this.act(agent, api.approveSourceAgent, agent.machineName + " approved", "could not be approved");
    },
    /** It stays joined and approved; Inistate reads nothing through it until it is on again. */
    async turnOff(agent) {
      if (this.busyId) return;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Turn " + agent.machineName + " off?",
        text: "Inistate stops reading through " + agent.machineName + " until you turn it back on. It stays "
          + "joined and approved, nothing in its databases changes, and the modules that read through it "
          + "show their source as off meanwhile.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: "Turn off", default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true) return;
      await this.act(agent, api.turnOffSourceAgent, agent.machineName + " turned off", "could not be turned off");
    },
    async turnOn(agent) {
      if (this.busyId) return;
      const done = await this.act(agent, api.turnOnSourceAgent,
        agent.machineName + " turned on. It checks in within a minute.", "could not be turned on");
      if (done && !this._isDestroyed) this.recheckUntilOnline(agent.agentId);
    },
    /**
     * A connector turned on serves again at its next check-in, within a minute: the list again every
     * 5 s until its connections are online, for 90 s at most.
     */
    recheckUntilOnline(agentId) {
      clearTimeout(this.recheckTimer);
      this.turningOn = agentId;
      const until = Date.now() + 90000;
      const tick = async () => {
        await this.load();
        if (this._isDestroyed) return;
        const waiting = this.connections.some((c) => c.transport === "agent" && c.agent
          && sameId(c.agent.agentId, agentId) && !c.agent.online);
        if (waiting && Date.now() < until) this.recheckTimer = setTimeout(tick, 5000);
        else this.turningOn = "";
      };
      this.recheckTimer = setTimeout(tick, 5000);
    },
    /** One call for one connector, then the list again, as the call changed it. True when it went through. */
    async act(agent, call, done, failed) {
      this.busyId = agent.agentId;
      this.error = "";
      try {
        await call(this.workspaceId, agent.agentId);
        this.$toasted.success(done);
        await this.load();
        return true;
      } catch (err) {
        this.error = this.message(err, agent.machineName + " " + failed + ".");
        return false;
      } finally {
        this.busyId = "";
      }
    },
    goAutoCount() {
      this.$router.push({ name: "settings", params: { section: "autocount" } }).catch(() => {});
    },
  },
  watch: {
    workspaceId() {
      this.loaded = false;
      this.connections = [];
      this.agents = [];
      this.load();
    },
  },
  created() {
    this.recheckTimer = null;
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.recheckTimer);
  },
};
</script>

<style lang="scss" scoped>
.fts-sources-empty {
  margin: 0;
  color: var(--fts-settings-mute);
}

/* A table wider than a phone scrolls inside its card instead of pushing the page sideways. */
.fts-sources-scroll {
  overflow-x: auto;
}

.fts-sources-scroll .fts-settings-table td strong {
  font-weight: 600;
}

.fts-sources-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.fts-sources-agent {
  flex-wrap: wrap;
}

.fts-sources-agent-side {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.fts-sources-approve-autocount {
  font-size: 0.8125rem;
}
</style>
